import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Tag, X, Trash2, Edit, RefreshCw, TrendingUp, BarChart3, Plus, Home, FileText, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DailyReport {
  id: number;
  report_date: string;
  total_sales: number;
  customer_count: number;
  weather: string;
  ai_tags: string;
  ai_generated_report: string;
  user_comment?: string;
}

interface WeeklyReportData {
  report: string;
  summary: {
    weekStartDate: string;
    weekEndDate: string;
    totalSales: number;
    totalCustomers: number;
    avgDailySales: number;
    daysCount: number;
  };
}

interface MonthlyReportData {
  report: string;
  summary: {
    year: number;
    month: number;
    totalSales: number;
    totalCustomers: number;
    avgDailySales: number;
    bestDay: {
      date: string;
      sales: number;
    };
    daysCount: number;
  };
}

const DailyReportCalendar: React.FC = () => {
  const navigate = useNavigate();

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [reports, setReports] = useState<{ [key: string]: DailyReport }>({});
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [editComment, setEditComment] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // 週報・月報用の状態
  const [weeklyReport, setWeeklyReport] = useState<WeeklyReportData | null>(null);
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReportData | null>(null);
  const [generatingWeekly, setGeneratingWeekly] = useState(false);
  const [generatingMonthly, setGeneratingMonthly] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<number>(1); // 週選択用

  useEffect(() => {
    fetchReports();
  }, [currentYear, currentMonth]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/daily-reports?year=${currentYear}&month=${currentMonth}`);
      const data = await res.json();

      if (data.success) {
        const reportsMap: { [key: string]: DailyReport } = {};
        data.data.forEach((report: DailyReport) => {
          const day = new Date(report.report_date).getDate();
          reportsMap[day] = report;
        });
        setReports(reportsMap);
      }
    } catch (error) {
      console.error('日報取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDelete = async () => {
    if (!selectedReport) return;

    if (!confirm(`${selectedReport.report_date}の日報を削除しますか？`)) return;

    try {
      const res = await fetch(`/api/daily-reports/${selectedReport.report_date}`, {
        method: 'DELETE'
      });

      const data = await res.json();
      if (data.success) {
        alert('日報を削除しました');
        setSelectedReport(null);
        fetchReports(); // 再読み込み
      } else {
        alert('削除に失敗しました: ' + data.message);
      }
    } catch (error) {
      console.error('削除エラー:', error);
      alert('エラーが発生しました');
    }
  };

  const handleEdit = () => {
    if (!selectedReport) return;
    setEditComment(selectedReport.user_comment || '');
    setIsEditing(true);
  };

  const handleRegenerate = async () => {
    if (!selectedReport || !editComment.trim()) {
      alert('コメントを入力してください');
      return;
    }

    setRegenerating(true);
    try {
      const res = await fetch(`/api/daily-reports/${selectedReport.report_date}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_comment: editComment })
      });

      const data = await res.json();
      if (data.success) {
        alert('日報を再生成しました！');
        setIsEditing(false);
        fetchReports(); // 再読み込み
        // 選択中の日報を更新
        const updatedReport = { ...selectedReport, ai_generated_report: data.data.report, user_comment: editComment };
        setSelectedReport(updatedReport);
      } else {
        alert('再生成に失敗しました: ' + data.message);
      }
    } catch (error) {
      console.error('再生成エラー:', error);
      alert('エラーが発生しました');
    } finally {
      setRegenerating(false);
    }
  };

  // 前日の日報に移動
  const handlePrevDay = () => {
    if (!selectedReport) return;

    const currentDate = new Date(selectedReport.report_date);
    const prevDate = new Date(currentDate);
    prevDate.setDate(prevDate.getDate() - 1);

    const prevDay = prevDate.getDate();
    const prevMonth = prevDate.getMonth() + 1;
    const prevYear = prevDate.getFullYear();

    // 同じ月内の場合
    if (prevMonth === currentMonth && prevYear === currentYear) {
      const prevReport = reports[prevDay];
      if (prevReport) {
        setSelectedReport(prevReport);
        setEditComment(prevReport.user_comment || '');
      } else {
        alert('前日の日報がありません');
      }
    } else {
      // 月をまたぐ場合は月を変更してからデータを取得
      setCurrentMonth(prevMonth);
      setCurrentYear(prevYear);
      // データ取得後に前日の日報を選択するため、useEffectで処理する必要あり
      // とりあえず月だけ変更する
    }
  };

  // 翌日の日報に移動
  const handleNextDay = () => {
    if (!selectedReport) return;

    const currentDate = new Date(selectedReport.report_date);
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + 1);

    const nextDay = nextDate.getDate();
    const nextMonth = nextDate.getMonth() + 1;
    const nextYear = nextDate.getFullYear();

    // 同じ月内の場合
    if (nextMonth === currentMonth && nextYear === currentYear) {
      const nextReport = reports[nextDay];
      if (nextReport) {
        setSelectedReport(nextReport);
        setEditComment(nextReport.user_comment || '');
      } else {
        alert('翌日の日報がありません');
      }
    } else {
      // 月をまたぐ場合は月を変更してからデータを取得
      setCurrentMonth(nextMonth);
      setCurrentYear(nextYear);
    }
  };

  // 週報生成
  const handleGenerateWeekly = async () => {
    // 既存の週報をチェック
    try {
      const checkRes = await fetch('/api/daily-reports/weekly/list?limit=100');
      const checkData = await checkRes.json();

      if (checkData.success) {
        // 同じ年月の週報が存在するかチェック
        const existingReport = checkData.data.find((r: any) => {
          const startDate = new Date(r.week_start_date);
          return startDate.getFullYear() === currentYear &&
                 startDate.getMonth() + 1 === currentMonth;
        });

        if (existingReport) {
          const shouldContinue = window.confirm(
            `${currentYear}年${currentMonth}月の週報が既に存在します。\n\n再生成しますか？（既存の週報は上書きされます）`
          );
          if (!shouldContinue) return;
        }
      }
    } catch (error) {
      console.error('週報チェックエラー:', error);
    }

    setGeneratingWeekly(true);
    try {
      const res = await fetch('/api/daily-reports/weekly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: currentYear, month: currentMonth, week: selectedWeek })
      });

      const data = await res.json();
      if (data.success) {
        setWeeklyReport(data.data);
      } else {
        alert('週報生成に失敗しました: ' + data.message);
      }
    } catch (error) {
      console.error('週報生成エラー:', error);
      alert('エラーが発生しました');
    } finally {
      setGeneratingWeekly(false);
    }
  };

  // 月報生成
  const handleGenerateMonthly = async () => {
    // 既存の月報をチェック
    try {
      const checkRes = await fetch(`/api/daily-reports/monthly/list?year=${currentYear}&limit=12`);
      const checkData = await checkRes.json();

      if (checkData.success) {
        // 同じ年月の月報が存在するかチェック
        const existingReport = checkData.data.find((r: any) =>
          r.year === currentYear && r.month === currentMonth
        );

        if (existingReport) {
          const shouldContinue = window.confirm(
            `${currentYear}年${currentMonth}月の月報が既に存在します。\n\n再生成しますか？（既存の月報は上書きされます）`
          );
          if (!shouldContinue) return;
        }
      }
    } catch (error) {
      console.error('月報チェックエラー:', error);
    }

    setGeneratingMonthly(true);
    try {
      const res = await fetch('/api/daily-reports/monthly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: currentYear, month: currentMonth })
      });

      const data = await res.json();
      if (data.success) {
        setMonthlyReport(data.data);
      } else {
        alert('月報生成に失敗しました: ' + data.message);
      }
    } catch (error) {
      console.error('月報生成エラー:', error);
      alert('エラーが発生しました');
    } finally {
      setGeneratingMonthly(false);
    }
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <CalendarIcon className="text-orange-500" />
          📅 日報カレンダー
        </h1>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
        >
          <Home size={20} />
          HOME
        </button>
      </div>

      {/* 月切り替え + 週報・月報ボタン */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6 print:hidden">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded">
            <ChevronLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold">
            {currentYear}年 {currentMonth}月
          </h2>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded">
            <ChevronRight size={24} />
          </button>
        </div>

        {/* 日報生成ボタン */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/reports/daily')}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg font-bold text-lg hover:from-orange-600 hover:to-orange-700 flex items-center justify-center gap-2 shadow-lg transform hover:scale-105 transition-all"
          >
            <FileText size={24} />
            📝 今日の日報を作成
          </button>
        </div>

        <div className="flex gap-3">
          <div className="flex-1 flex gap-2">
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>第1週</option>
              <option value={2}>第2週</option>
              <option value={3}>第3週</option>
              <option value={4}>第4週</option>
              <option value={5}>第5週</option>
            </select>
            <button
              onClick={handleGenerateWeekly}
              disabled={generatingWeekly}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <TrendingUp size={18} className={generatingWeekly ? 'animate-spin' : ''} />
              {generatingWeekly ? '週報生成中...' : '📊 週報を作成'}
            </button>
          </div>
          <button
            onClick={handleGenerateMonthly}
            disabled={generatingMonthly}
            className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <BarChart3 size={18} className={generatingMonthly ? 'animate-spin' : ''} />
            {generatingMonthly ? '月報生成中...' : '📅 月報を作成'}
          </button>
          <button
            onClick={() => navigate('/reports/list')}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 flex items-center justify-center gap-2"
          >
            <List size={18} />
            📋 週報・月報一覧
          </button>
        </div>
      </div>

      {/* カレンダーグリッド */}
      <div className="bg-white rounded-lg shadow-md p-4 print:hidden">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['日', '月', '火', '水', '木', '金', '土'].map(day => (
            <div key={day} className="text-center font-bold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {emptyDays.map(i => (
            <div key={`empty-${i}`} className="h-32"></div>
          ))}

          {daysArray.map(day => {
            const report = reports[day];
            const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            // JST基準で今日以前かチェック
            const jstNow = new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
            const jstToday = jstNow.toISOString().split('T')[0];
            const isPast = dateStr <= jstToday;

            return (
              <div
                key={day}
                className={`h-32 border rounded-lg p-2 transition-all relative group
                  ${report ? 'bg-orange-50 border-orange-300 hover:bg-orange-100 cursor-pointer' : isPast ? 'bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer' : 'bg-gray-50 border-gray-200'}
                  ${selectedReport?.report_date === report?.report_date ? 'ring-2 ring-orange-500' : ''}
                `}
              >
                <div className="font-bold text-lg mb-1">{day}</div>
                {report ? (
                  <div className="text-xs" onClick={() => setSelectedReport(report)}>
                    <div className="font-semibold text-green-600">
                      ¥{report.total_sales.toLocaleString()}
                    </div>
                    <div className="text-gray-600">{report.customer_count}人</div>
                    {report.ai_tags && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {JSON.parse(report.ai_tags).slice(0, 2).map((tag: string, i: number) => (
                          <span key={i} className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-xs">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : isPast && (
                  <button
                    onClick={() => navigate(`/reports/daily?date=${dateStr}`)}
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 bg-opacity-10 rounded-lg"
                  >
                    <div className="bg-orange-500 text-white px-3 py-1 rounded-lg flex items-center gap-1 text-xs font-semibold">
                      <Plus size={14} />
                      日報作成
                    </div>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 日報詳細モーダル */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 print:relative print:bg-transparent print:p-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 print:shadow-none print:max-w-full print:max-h-full print:p-0 print:border-0 print:rounded-none">
            <button
              onClick={() => setSelectedReport(null)}
              className="float-right text-gray-500 hover:text-gray-700 print:hidden"
            >
              <X size={24} />
            </button>

            <div className="clear-both">
              <div className="border-b-4 border-orange-500 pb-4 mb-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handlePrevDay}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors print:hidden"
                    title="前日"
                  >
                    <ChevronLeft size={24} className="text-gray-600" />
                  </button>
                  <div className="flex-1 text-center">
                    <h2 className="text-2xl font-bold text-orange-600">
                      🍖 和とん日報
                    </h2>
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedReport.report_date} / {selectedReport.weather}
                    </p>
                  </div>
                  <button
                    onClick={handleNextDay}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors print:hidden"
                    title="翌日"
                  >
                    <ChevronRight size={24} className="text-gray-600" />
                  </button>
                </div>
              </div>

              {isEditing ? (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    コメントを編集（再生成されます）
                  </label>
                  <textarea
                    value={editComment}
                    onChange={(e) => setEditComment(e.target.value)}
                    className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={handleRegenerate}
                      disabled={regenerating}
                      className="flex-1 bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <RefreshCw size={18} className={regenerating ? 'animate-spin' : ''} />
                      {regenerating ? '再生成中...' : '再生成する'}
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              ) : (
                <div className="prose max-w-none whitespace-pre-wrap">
                  {selectedReport.ai_generated_report}
                </div>
              )}

              <div className="mt-6 flex gap-3 print:hidden">
                <button
                  onClick={handlePrint}
                  className="flex-1 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 flex items-center justify-center gap-2"
                >
                  🖨️ 印刷
                </button>
                <button
                  onClick={handleEdit}
                  className="flex-1 bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 flex items-center justify-center gap-2"
                >
                  <Edit size={18} />
                  編集
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  削除
                </button>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 週報モーダル */}
      {weeklyReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 print:contents">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 print:shadow-none print:max-w-full print:max-h-full print:overflow-visible print:p-8 print:m-0 print:border-0 print:rounded-none">
            <button
              onClick={() => setWeeklyReport(null)}
              className="float-right text-gray-500 hover:text-gray-700 print:hidden"
            >
              <X size={24} />
            </button>

            <div className="clear-both">
              <div className="border-b-4 border-blue-500 pb-4 mb-6">
                <h2 className="text-2xl font-bold text-blue-600">
                  📊 和とん週報
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  {weeklyReport.summary.weekStartDate} 〜 {weeklyReport.summary.weekEndDate}
                </p>
                <div className="mt-2 text-sm text-gray-700">
                  <span className="mr-4">💰 合計売上: ¥{weeklyReport.summary.totalSales.toLocaleString()}</span>
                  <span className="mr-4">👥 合計客数: {weeklyReport.summary.totalCustomers}人</span>
                  <span>📈 平均売上: ¥{weeklyReport.summary.avgDailySales.toLocaleString()}/日</span>
                </div>
              </div>

              <div className="prose max-w-none whitespace-pre-wrap">
                {weeklyReport.report}
              </div>

              <div className="mt-6 flex gap-3 print:hidden">
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 flex items-center justify-center gap-2"
                >
                  🖨️ 印刷
                </button>
                <button
                  onClick={() => setWeeklyReport(null)}
                  className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 月報モーダル */}
      {monthlyReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 print:contents">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 print:shadow-none print:max-w-full print:max-h-full print:overflow-visible print:p-8 print:m-0 print:border-0 print:rounded-none">
            <button
              onClick={() => setMonthlyReport(null)}
              className="float-right text-gray-500 hover:text-gray-700 print:hidden"
            >
              <X size={24} />
            </button>

            <div className="clear-both">
              <div className="border-b-4 border-purple-500 pb-4 mb-6">
                <h2 className="text-2xl font-bold text-purple-600">
                  📅 和とん月報
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  {monthlyReport.summary.year}年{monthlyReport.summary.month}月
                </p>
                <div className="mt-2 text-sm text-gray-700">
                  <span className="mr-4">💰 合計売上: ¥{monthlyReport.summary.totalSales.toLocaleString()}</span>
                  <span className="mr-4">👥 合計客数: {monthlyReport.summary.totalCustomers}人</span>
                  <span className="mr-4">📈 平均売上: ¥{monthlyReport.summary.avgDailySales.toLocaleString()}/日</span>
                  <span>🏆 最高売上: {monthlyReport.summary.bestDay.date} (¥{monthlyReport.summary.bestDay.sales.toLocaleString()})</span>
                </div>
              </div>

              <div className="prose max-w-none whitespace-pre-wrap">
                {monthlyReport.report}
              </div>

              <div className="mt-6 flex gap-3 print:hidden">
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-purple-500 text-white py-2 px-4 rounded hover:bg-purple-600 flex items-center justify-center gap-2"
                >
                  🖨️ 印刷
                </button>
                <button
                  onClick={() => setMonthlyReport(null)}
                  className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReportCalendar;
